import { supabase } from './supabase';
import { normalizeHevyName } from './parsers';

// ─── SUPABASE DB ──────────────────────────────────────────────────────────────
export const EMPTY_DB = {sessions:[],weightLog:[],schede:[]};

export async function fetchDB(userId) {
  const [sessRes,wlogRes,schRes] = await Promise.all([
    supabase.from('sessions').select('*').eq('user_id',userId).order('date',{ascending:false}),
    supabase.from('weight_log').select('*').eq('user_id',userId).order('date',{ascending:false}),
    supabase.from('schede').select('*').eq('user_id',userId),
  ]);
  if(sessRes.error) { console.error('[DB] fetchDB sessions error:', sessRes.error?.message); throw sessRes.error; }
  if(wlogRes.error) { console.error('[DB] fetchDB weight_log error:', wlogRes.error?.message); throw wlogRes.error; }
  if(schRes.error)  { console.error('[DB] fetchDB schede error:', schRes.error?.message); throw schRes.error; }
  return {
    sessions:(sessRes.data||[]).map(s=>({
      id:s.id,date:s.date,workoutName:s.workout_name,duration:s.duration??null,
      exercises:(s.exercises||[]).map(e=>({...e,name:normalizeHevyName(e.name||'')})),
    })),
    weightLog:(wlogRes.data||[]).map(w=>({
      id:w.id,date:w.date,weight:w.weight,
      ...(w.fat_pct!=null?{fatPct:w.fat_pct}:{}),
      ...(w.lean_mass!=null?{leanMass:w.lean_mass}:{}),
    })),
    schede:(schRes.data||[]).map(s=>({id:s.id,name:s.name,exercises:s.exercises||[]})),
  };
}

async function runOp(label, promise) {
  const res = await promise;
  if (res.error) console.error(`[DB] ${label} failed:`, res.error?.message);
  return res;
}

async function batchInsert(table, rows, chunkSize = 50) {
  for (let i = 0; i < rows.length; i += chunkSize) {
    const { error } = await supabase.from(table).insert(rows.slice(i, i + chunkSize));
    if (error) { console.error(`[DB] ${table} batch insert failed:`, error?.message); throw error; }
  }
}

export async function syncToSupabase(prev, next, userId) {
  // sessions
  const pS=new Map((prev.sessions||[]).map(s=>[String(s.id),s]));
  const nS=new Map((next.sessions||[]).map(s=>[String(s.id),s]));
  const toInsert=[];
  const updateOps=[];
  for(const[id,s]of nS){
    if(!pS.has(id)) toInsert.push({id,user_id:userId,date:s.date,workout_name:s.workoutName||'',exercises:s.exercises||[],duration:s.duration??null});
    else{const p=pS.get(id);if(p.date!==s.date||p.workoutName!==s.workoutName||JSON.stringify(p.exercises)!==JSON.stringify(s.exercises)||(p.duration??null)!==(s.duration??null))
      updateOps.push(runOp(`sessions update ${id}`, supabase.from('sessions').update({date:s.date,workout_name:s.workoutName||'',exercises:s.exercises||[],duration:s.duration??null}).eq('id',id).eq('user_id',userId)));}
  }
  const deleteOps=[];
  for(const id of pS.keys()) if(!nS.has(id)) deleteOps.push(runOp(`sessions delete ${id}`, supabase.from('sessions').delete().eq('id',id).eq('user_id',userId)));
  if(toInsert.length) await batchInsert('sessions', toInsert);
  await Promise.all([...updateOps, ...deleteOps]);

  // weight_log
  const pW=new Map((prev.weightLog||[]).map(w=>[String(w.id),w]));
  const nW=new Map((next.weightLog||[]).map(w=>[String(w.id),w]));
  const wToInsert=[];
  const wOps=[];
  for(const[id,w]of nW){
    const row={id,user_id:userId,date:w.date,weight:w.weight,fat_pct:w.fatPct??null,lean_mass:w.leanMass??null};
    if(!pW.has(id)) wToInsert.push(row);
    else{const p=pW.get(id);
      if(p.weight!==w.weight||p.date!==w.date||(p.fatPct??null)!==(w.fatPct??null)||(p.leanMass??null)!==(w.leanMass??null))
        wOps.push(runOp(`weight_log update ${id}`, supabase.from('weight_log').update({date:w.date,weight:w.weight,fat_pct:w.fatPct??null,lean_mass:w.leanMass??null}).eq('id',id).eq('user_id',userId)));}
  }
  for(const id of pW.keys()) if(!nW.has(id)) wOps.push(runOp(`weight_log delete ${id}`, supabase.from('weight_log').delete().eq('id',id).eq('user_id',userId)));
  if(wToInsert.length) await batchInsert('weight_log', wToInsert);
  await Promise.all(wOps);

  // Schede — replace-all: delete then reinsert avoids all diff/id ambiguity
  const { error: schDelErr } = await supabase.from('schede').delete().eq('user_id', userId);
  if (schDelErr) {
    console.error('[DB] schede delete-all failed:', schDelErr?.message);
  } else if (next.schede?.length) {
    const ts = Date.now();
    const rows = next.schede.map((s, i) => ({ id: ts + i, user_id: userId, name: s.name, exercises: s.exercises || [] }));
    const { error: schInsErr } = await supabase.from('schede').insert(rows);
    if (schInsErr) console.error('[DB] schede insert-all failed:', schInsErr?.message);
    else console.log('[DB] schede synced:', rows.map(r => r.name));
  } else {
    console.log('[DB] schede cleared (empty)');
  }
}

// saveDB è una ref aggiornata dall'App — i componenti la chiamano come prima
let _syncFn = null;
export function setSyncFn(fn) { _syncFn = fn; }
export function saveDB(newDb) {
  if (_syncFn) return _syncFn(newDb);
  console.warn('[DB] saveDB called but _syncFn is null — data will NOT be persisted');
  return Promise.resolve();
}
